import * as babylon from "@babylonjs/core";

import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { TopicData } from "@/shared/state/visualization/schema/v1/topic-data";

import {
  messagePathKey,
  TopicIdToMessagePaths,
  TopicIdToMessagePathValues,
} from "../../../DataServer/messaging";
import { GradientOptions } from "../../assetBuilders/gradient";
import {
  createVectorFieldMesh,
  VECTOR_FIELD_BASE_WIDTH,
} from "../../assetBuilders/vectorField";
import * as components from "../../components";
import { markLoadFailed } from "../../components/reloadable";
import { BabylonContext, BabylonState } from "../../state";

/**
 * Loads vector data for any vector fields whose caches have gone stale.
 */
export function reloadStaleVectorFieldCaches(state: BabylonState) {
  for (const entity of Object.values(state.entities)) {
    if (
      !entity.vectorField ||
      entity.vectorField.cache.load.state !== "stale"
    ) {
      continue;
    }
    const vectorField = entity.vectorField;
    const load = vectorField.cache.load;
    // Abort any previous load
    load.abortController.abort();

    load.state = "loading";
    const abortController = new AbortController();
    load.abortController = abortController;
    vectorField.cache.allVectors = [];

    reloadVectorCache(vectorField, state.context, abortController.signal).catch(
      (e) => {
        if (isAbortError(e) && abortController.signal.aborted) {
          return;
        }
        ErrorMonitoringService.captureError(e);
        markLoadFailed(load, abortController.signal);
      },
    );
  }
}

/**
 * Builds the vector field mesh for entities whose vector and position caches
 * are both loaded and whose mesh is stale.
 */
export function reloadStaleVectorFieldMeshes(state: BabylonState) {
  for (const entity of Object.values(state.entities)) {
    if (
      !entity.vectorField ||
      !entity.globalPosition ||
      !entity.vectorField.meshStale ||
      entity.vectorField.cache.load.state !== "loaded" ||
      entity.globalPosition.cache.load.state !== "loaded"
    ) {
      continue;
    }

    const vf = entity.vectorField;
    vf.mesh?.material?.dispose();
    vf.mesh?.dispose();
    vf.currentMesh?.material?.dispose();
    vf.currentMesh?.dispose();

    // Sampled from the *global* trajectory, so the field is drawn where the
    // entity actually was rather than at offsets in its parent's frame. The
    // vectors themselves need no transform: they are translation-invariant, and
    // there is no rotation in the hierarchy yet.
    const mean = entity.globalPosition.mean;
    const allPositions = entity.globalPosition.cache.allPoints.map((p) =>
      p.subtract(mean),
    );
    const { alignedPositions, alignedVectors } = alignVectorsToPositions(
      entity.globalPosition.cache.logtimes,
      allPositions,
      vf.cache.logtimes,
      vf.cache.allVectors,
    );

    const stride = Math.max(1, vf.spec.stride ?? 1);
    const lineWidth = vf.spec.lineWidth;

    const displayMode = vf.spec.displayMode ?? "current_only";
    const showField = displayMode === "field_only";
    const showCurrent = displayMode === "current_only";

    // Build gradient options if the user has enabled gradient mode.
    let gradient: GradientOptions | undefined;
    if (vf.spec.gradientEnabled && vf.spec.colorLow && vf.spec.colorHigh) {
      let minMag = Infinity;
      let maxMag = -Infinity;
      for (let i = 0; i < alignedVectors.length; i += stride) {
        const mag = alignedVectors[i].length();
        if (mag < minMag) {
          minMag = mag;
        }
        if (mag > maxMag) {
          maxMag = mag;
        }
      }
      gradient = {
        colorLow: babylon.Color3.FromHexString(vf.spec.colorLow),
        colorHigh: babylon.Color3.FromHexString(vf.spec.colorHigh),
        minMagnitude: minMag,
        maxMagnitude: maxMag,
      };
    }

    if (
      lineWidth > 0 &&
      isFinite(lineWidth) &&
      alignedPositions.length > 0 &&
      alignedVectors.length > 0
    ) {
      if (showField) {
        vf.mesh = createVectorFieldMesh(
          `${entity.id}.vectorField`,
          vf.spec.color,
          lineWidth,
          alignedPositions,
          alignedVectors,
          stride,
          state.context.scene,
          gradient,
        );
      }

      if (showCurrent) {
        // Create a single-segment greased line for the current-time vector indicator.
        // Its geometry is updated every frame in the render loop.
        // Rendered as a triangle: fat base (8× width) tapering to a point.
        const initialSegment = [babylon.Vector3.Zero(), babylon.Vector3.Zero()];
        const currentWidths = [
          VECTOR_FIELD_BASE_WIDTH,
          VECTOR_FIELD_BASE_WIDTH,
          0,
          0,
        ];
        vf.currentMesh = babylon.CreateGreasedLine(
          `${entity.id}.vectorField.current`,
          {
            points: initialSegment,
            widths: currentWidths,
            widthDistribution:
              babylon.GreasedLineMeshWidthDistribution.WIDTH_DISTRIBUTION_NONE,
          },
          {
            color: babylon.Color3.FromHexString(vf.spec.color),
            width: vf.spec.lineWidth,
            sizeAttenuation: true,
          },
          state.context.scene,
        ) as babylon.GreasedLineMesh;
      }
    }
    vf.gradient = gradient;
    vf.meshStale = false;
  }
}

/**
 * Pairs each vector with its nearest position by timestamp.
 *
 * Returns parallel arrays of positions and vectors, one entry per vector timestamp.
 * Each vector is matched to the latest position whose timestamp is at or before
 * the vector's timestamp. If no such position exists (i.e. the vector precedes
 * all positions), the first position is used as a fallback.
 */
export function alignVectorsToPositions(
  posLogtimes: bigint[],
  positions: babylon.Vector3[],
  vecLogtimes: bigint[],
  vectors: babylon.Vector3[],
): { alignedPositions: babylon.Vector3[]; alignedVectors: babylon.Vector3[] } {
  if (vecLogtimes.length === 0 || posLogtimes.length === 0) {
    return { alignedPositions: [], alignedVectors: [] };
  }
  const alignedPositions: babylon.Vector3[] = [];
  const alignedVectors: babylon.Vector3[] = [];
  let pi = 0;
  for (let vi = 0; vi < vecLogtimes.length && vi < vectors.length; vi++) {
    // Advance position index to the latest position at or before this vector time
    while (
      pi < posLogtimes.length - 1 &&
      posLogtimes[pi + 1] <= vecLogtimes[vi]
    ) {
      pi++;
    }
    alignedPositions.push(positions[pi]);
    alignedVectors.push(vectors[vi]);
  }
  return { alignedPositions, alignedVectors };
}

async function reloadVectorCache(
  vectorField: components.VectorField,
  context: BabylonContext,
  signal: AbortSignal,
) {
  const { right, up, forward, rightScale, upScale, forwardScale } =
    vectorField.spec;
  const toRead = [right, up, forward];

  // If all components are constants, produce a single constant vector.
  if (
    typeof right === "number" &&
    typeof forward === "number" &&
    typeof up === "number"
  ) {
    const vector = new babylon.Vector3(
      right * rightScale,
      up * upScale,
      forward * forwardScale,
    );
    vectorField.cache.logtimes = [0n];
    vectorField.cache.allVectors = [vector];
    vectorField.cache.load.state = "loaded";
    vectorField.meshStale = true;
    return;
  }

  const topicIdToMessagePaths: TopicIdToMessagePaths = {};
  for (const topicData of toRead) {
    if (typeof topicData === "number") {
      continue;
    }
    const mp = {
      id: topicData.messagePath.id,
      parts: topicData.messagePath.parts,
    };
    topicIdToMessagePaths[topicData.topic.id] ??= {};
    topicIdToMessagePaths[topicData.topic.id][messagePathKey(mp)] = mp;
  }

  const topicIdToMessagePathValues = await context.dataServer.getAllData(
    topicIdToMessagePaths,
    signal,
  );
  signal.throwIfAborted();

  const xs = extractValues(topicIdToMessagePathValues, right, rightScale);
  const ys = extractValues(topicIdToMessagePathValues, up, upScale);
  const zs = extractValues(topicIdToMessagePathValues, forward, forwardScale);

  const { logtimes, vectors } = mergeComponentTimelines(xs, ys, zs);

  vectorField.cache.load.state = "loaded";
  vectorField.cache.logtimes = logtimes;
  vectorField.cache.allVectors = vectors;
  vectorField.meshStale = true;
}

/**
 * One channel's timeline: its timestamps, and its values at them.
 *
 * Both arrays are the response's own, handed back by reference. Neither is
 * copied, because a copy here is paid once per *bound channel* rather than
 * once per topic: an articulated robot binds a channel per joint, all reading
 * the same topic, and each timeline runs to tens of thousands of samples. That
 * makes these the largest allocations on the reload path, and they buy nothing.
 *
 * The one unavoidable copy is a scaled channel. Multiplying every sample has to
 * produce a new array, so `scale` of anything but 1 costs one — and 1 is the
 * overwhelmingly common case, since a scale exists to convert units (degrees to
 * radians, say) and most bindings are already in the units the model declares.
 *
 * What makes the sharing safe is that a timeline is read-only once returned.
 * The callers that build a merged timeline (`mergeComponentTimelines`,
 * `reloadRotations`, `reloadPositions`) push into their own accumulator, and a
 * cache that goes stale is handed a new array rather than emptied in place. The
 * one thing that must never happen is a caller writing into what it gets back.
 *
 * Sharing is also only safe because the response is whole by the time it is
 * shared — a promise-memoized read, not a container published before it is
 * filled. That distinction is the subject of `DataServer`'s class comment, and
 * getting it wrong is what once froze a joint at a stale sample.
 */
export function extractValues(
  topicIdToData: TopicIdToMessagePathValues,
  component: TopicData | number,
  scale: number,
): { logtimes: bigint[]; values: number[] } {
  if (typeof component === "number") {
    return { logtimes: [0n], values: [component * scale] };
  } else {
    const topicData = topicIdToData[component.topic.id];
    const rawValues = topicData?.values[messagePathKey(component.messagePath)];

    if (!topicData || !rawValues || topicData.logtimes.length === 0) {
      return {
        logtimes: [0n],
        values: [0],
      };
    }

    return {
      logtimes: topicData.logtimes,
      values: scale === 1 ? rawValues : rawValues.map((v) => v * scale),
    };
  }
}

/**
 * Merges three independent component timelines (x, y, z) into a single
 * timeline of Vector3 values. Uses sample-and-hold: each component's
 * latest value is held forward until its next sample arrives.
 */
export function mergeComponentTimelines(
  xs: { logtimes: bigint[]; values: number[] },
  ys: { logtimes: bigint[]; values: number[] },
  zs: { logtimes: bigint[]; values: number[] },
): { logtimes: bigint[]; vectors: babylon.Vector3[] } {
  const vectors: babylon.Vector3[] = [];
  const logtimes: bigint[] = [];

  let xi = 0;
  let yi = 0;
  let zi = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const t = bigIntMax(xs.logtimes[xi], ys.logtimes[yi], zs.logtimes[zi]);
    const vector = new babylon.Vector3(
      xs.values[xi],
      ys.values[yi],
      zs.values[zi],
    );
    logtimes.push(t);
    vectors.push(vector);

    // If all indices have reached the end, we've emitted the final sample.
    if (
      xi >= xs.logtimes.length - 1 &&
      yi >= ys.logtimes.length - 1 &&
      zi >= zs.logtimes.length - 1
    ) {
      break;
    }

    const nextXTime = xs.logtimes.at(xi + 1);
    const nextYTime = ys.logtimes.at(yi + 1);
    const nextZTime = zs.logtimes.at(zi + 1);
    const nextTime = bigIntMin(nextXTime, nextYTime, nextZTime);
    if (nextXTime === nextTime && xi < xs.logtimes.length - 1) {
      xi++;
    }
    if (nextYTime === nextTime && yi < ys.logtimes.length - 1) {
      yi++;
    }
    if (nextZTime === nextTime && zi < zs.logtimes.length - 1) {
      zi++;
    }
  }

  return { logtimes, vectors };
}

/**
 * The sorted union of several sample timelines, with duplicates collapsed.
 *
 * Where `mergeComponentTimelines` merges the x/y/z of one entity, this merges
 * whole timelines across an entity and its ancestors: a child's absolute
 * trajectory has to be evaluated wherever *any* link in its chain has a sample,
 * or the composed path would cut the corners its parent rounds.
 *
 * Each input must already be sorted ascending, as position caches are. Empty
 * timelines contribute nothing, so a structural ancestor with no data is free.
 *
 * A k-way merge rather than concat-and-sort: the inputs are sorted, and the
 * result can be long (up to the sum of the input lengths, for a deep chain).
 */
export function mergeLogTimelines(timelines: bigint[][]): bigint[] {
  const nonEmpty = timelines.filter((timeline) => timeline.length > 0);
  if (nonEmpty.length === 0) {
    return [];
  }
  if (nonEmpty.length === 1) {
    return dedupeSorted(nonEmpty[0]);
  }

  const indices = Array.from<number>({ length: nonEmpty.length }).fill(0);
  const merged: bigint[] = [];

  for (;;) {
    let next: bigint | undefined;
    for (let i = 0; i < nonEmpty.length; i++) {
      const candidate = nonEmpty[i][indices[i]];
      if (candidate !== undefined && (next === undefined || candidate < next)) {
        next = candidate;
      }
    }
    if (next === undefined) {
      return merged;
    }
    merged.push(next);
    // Advance every timeline sitting on this time, which is what collapses
    // duplicates across inputs.
    for (let i = 0; i < nonEmpty.length; i++) {
      while (nonEmpty[i][indices[i]] === next) {
        indices[i]++;
      }
    }
  }
}

/** Drops repeated values from an already-sorted timeline. */
function dedupeSorted(timeline: bigint[]): bigint[] {
  return timeline.filter(
    (time, index) => index === 0 || time !== timeline[index - 1],
  );
}

/**
 * Returns the maximum of the given bigint values.
 *
 * Unlike `bigIntMin`, this function does not handle `undefined` because it is
 * only called on indices that are guaranteed to be in-bounds: each component
 * array always has at least one element (ensured by `extractValues`), indices
 * start at 0, and the merge loop only advances an index when it has not yet
 * reached the last element.
 */
export function bigIntMax(...args: bigint[]) {
  return args.reduce((max, current) => (current < max ? max : current));
}

export function bigIntMin(...args: (bigint | undefined)[]) {
  return args.reduce((min, current) =>
    current === undefined || (min !== undefined && current > min)
      ? min
      : current,
  );
}
